import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import apiService from '../services/api';

const ReportSidebar = ({
    selectedReportId,
    onReportSelect,
    onNewReport,
    selectedCountry,
    refreshTrigger = 0,
    collapsed = false,
    onToggleCollapse
}) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Load report sessions
    useEffect(() => {
        loadReportSessions();
    }, [refreshTrigger]);

    const loadReportSessions = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await apiService.getReportSessions();
            setReports(response.data.reports || []);
        } catch (err) {
            console.error('Error loading report sessions:', err);
            setError('Failed to load report sessions');
        } finally {
            setLoading(false);
        }
    };

    const formatReportDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    const getReportTitle = (report) => {
        return report.title;
    };

    if (loading) {
        return (
            <Paper sx={{ width: 280, height: '100%', p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                width: collapsed ? 60 : 350,
                minWidth: collapsed ? 60 : 350,
                height: 'calc(100vh - 250px)', // Fixed height
                maxHeight: 'calc(100vh - 250px)',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out'
            }}
        >
            {/* Header */}
            <Box sx={{ p: collapsed ? 1 : 2, pb: 1, flexShrink: 0 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'space-between',
                    mb: collapsed ? 0 : 2,
                    flexDirection: collapsed ? 'column' : 'row',
                    gap: collapsed ? 1 : 0
                }}>
                    {!collapsed && (
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <AssessmentIcon sx={{ mr: 1 }} />
                            Report History
                        </Typography>
                    )}
                    {collapsed && (
                        <Tooltip title="Report History">
                            <AssessmentIcon color="primary" />
                        </Tooltip>
                    )}
                    {!collapsed && (
                        <Tooltip title="Generate a new report">
                            <IconButton
                                onClick={onNewReport}
                                color="primary"
                                size="small"
                            >
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {collapsed && (
                        <Tooltip title="Generate a new report">
                            <IconButton
                                onClick={onNewReport}
                                color="primary"
                                size="small"
                            >
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
                        <IconButton
                            onClick={onToggleCollapse}
                            size="small"
                            sx={{ mt: collapsed ? 1 : 0 }}
                        >
                            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!collapsed && <Divider />}

            {/* Error Message */}
            {!collapsed && error && (
                <Box sx={{ p: 2, flexShrink: 0 }}>
                    <Alert severity="error" size="small">
                        {error}
                    </Alert>
                </Box>
            )}

            {/* Reports List - Scrollable Area */}
            {!collapsed && (
            <Box sx={{
                flexGrow: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
            }}>
                {reports.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">
                            No reports generated yet.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Click "Generate Report" to create your first report!
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ 
                        overflow: 'auto', 
                        flexGrow: 1,
                        maxHeight: '100%'
                    }}>
                        <List dense sx={{ p: 0, pt: 1 }}>
                            {reports.map((report) => (
                                <ListItem key={report.id} disablePadding>
                                    <ListItemButton
                                        selected={selectedReportId === report.id}
                                        onClick={() => {
                                            console.log('Report clicked:', report.id);
                                            onReportSelect(report.id);
                                        }}
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            mx: 1, // Add horizontal margin for contained look
                                            borderRadius: 2, // Rounded corners
                                            '&.Mui-selected': {
                                                backgroundColor: 'primary.light',
                                                color: 'primary.contrastText',
                                                '&:hover': {
                                                    backgroundColor: 'primary.main',
                                                }
                                            },
                                            '&:hover': {
                                                borderRadius: 2, // Maintain rounded corners on hover
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" noWrap>
                                                    {getReportTitle(report)}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <Chip
                                                        label={report.year}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                            color: selectedReportId === report.id ? 'inherit' : 'text.secondary'
                                                        }}
                                                    />
                                                    <Chip
                                                        label={report.country}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                            color: selectedReportId === report.id ? 'inherit' : 'text.secondary'
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: selectedReportId === report.id ? 'inherit' : 'text.secondary'
                                                        }}
                                                    >
                                                        {formatReportDate(report.created_at)}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </Box>
            )}
        </Paper>
    );
};

export default ReportSidebar; 